import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { t } from '../i18n/index.js'
import type { CapturedPhoto } from '../lib/camera.js'
import { enqueue } from '../lib/outbox.js'
import { PhotoSlot } from './PhotoSlot.js'
import { WorkerShell, useWorkerTasks } from './shell.js'

export function ReportIssuePage() {
  const [params] = useSearchParams()
  const { tasks } = useWorkerTasks()

  const [objectId, setObjectId] = useState(params.get('objectId') ?? '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null)
  const [sent, setSent] = useState(false)
  const [saving, setSaving] = useState(false)

  // The worker picks from their own objects rather than a global list, it is
  // shorter, and it is all they have access to anyway.
  const places = [...new Map(tasks.map((task) => [task.objectId, task])).values()]
  const taskId = params.get('taskId') ?? undefined

  async function submit() {
    if (!objectId || !title.trim() || saving) return
    setSaving(true)
    try {
      await enqueue({
        id: crypto.randomUUID(),
        kind: 'issue',
        objectId,
        taskId,
        title: title.trim(),
        clientRef: crypto.randomUUID(),
        completedAtClient: new Date().toISOString(),
        note: description.trim() || undefined,
        photos: photo ? [{ kind: 'issue', blob: photo.blob, takenAt: photo.takenAt }] : [],
      })
      setSent(true)
    } finally {
      setSaving(false)
    }
  }

  if (sent) {
    return (
      <WorkerShell title={t('issue.title')}>
        <div className="notice success" style={{ fontSize: 17 }}>
          {t('issue.sent')}
        </div>
        <Link to="/app" className="wk-btn" style={{ marginTop: 20 }}>
          {t('app.back')}
        </Link>
      </WorkerShell>
    )
  }

  return (
    <WorkerShell title={t('issue.title')}>
      <label className="field">
        <span style={{ fontSize: 16 }}>{t('issue.where')}</span>
        <select value={objectId} onChange={(event) => setObjectId(event.target.value)} style={{ fontSize: 17, minHeight: 52 }}>
          <option value="">{t('common.choose')}</option>
          {places.map((task) => (
            <option key={task.objectId} value={task.objectId}>
              {task.siteName}, {task.label}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span style={{ fontSize: 16 }}>{t('issue.what')}</span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('issue.what_placeholder')}
          style={{ fontSize: 17, minHeight: 52 }}
        />
      </label>

      <label className="field">
        <span style={{ fontSize: 16 }}>{t('issue.details')}</span>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          style={{ fontSize: 17, minHeight: 90 }}
        />
      </label>

      <PhotoSlot label={t('issue.photo')} photo={photo} onChange={setPhoto} />

      <button
        type="button"
        className="wk-btn"
        disabled={!objectId || !title.trim() || saving}
        onClick={() => void submit()}
      >
        {t('issue.send')}
      </button>
    </WorkerShell>
  )
}
